/**
 * Codebase
 *
 * Displays a list of the user's codebases.
 * Allows users to:
 * - View codebase details
 * - Edit codebases in the code editor
 * - Delete existing codebases
 * - Create new codebases
 *
 * Fetches user details and codebases from the backend.
 *
 * @returns The codebase page.
 */
"use client";
import { cn } from "../../utils/util";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PopupGfg from "../ui/PopUp";

import Swal from "sweetalert2";
import codebaseService from "../../services/codebaseService";

const Codebase = () => {
  // State for holding list of codebases, loading status, and user details
  const [codebases, setCodebases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  const navigate = useNavigate();
  const [popupOpen, setPopupOpen] = useState(false);
  // Temporary static user ID, modify if needed as of now 4 users exist, [1,2,3,4]
  const userId = sessionStorage.getItem("userId");

  // Fetches user details from the backend
  const fetchUserDetails = async () => {
    const sessionToken = sessionStorage.getItem("session_token");
    const userId = sessionStorage.getItem("userId");

    if (!userId || !sessionToken) {
      console.error("User ID or session token is missing.");
      return;
    }

    try {
      const userDetails = await codebaseService.getUserDetails(
        userId,
        sessionToken
      );
      setUserDetails(userDetails);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  // Fetches codebases for the specific user from the backend
  const fetchCodebases = async () => {
    const sessionToken = sessionStorage.getItem("session_token");
    const userId = sessionStorage.getItem("userId");

    if (!userId || !sessionToken) {
      console.error("User ID or session token is missing.");
      setLoading(false);
      return;
    }

    try {
      const codebasesData = await codebaseService.getCodebasesForUser(
        userId,
        sessionToken
      );
      console.log("Fetched codebases:", codebasesData); // Debugging

      if (Array.isArray(codebasesData)) {
        const sortedCodebases = [...codebasesData].sort((a, b) => {
          if (!a.updated_at) return 1;
          if (!b.updated_at) return -1;

          return new Date(b.updated_at) - new Date(a.updated_at);
        });

        setCodebases(
          sortedCodebases.map((codebase) => ({
            id: codebase.codebaseId,
            name: codebase.codebase_name,
            isEditing: false,
            userId: codebase.userId,
            description: codebase.codebase_desc,
            updated_at: codebase.updated_at,
          }))
        );
      } else {
        console.warn("No codebases found.");
        setCodebases([]);
      }
    } catch (error) {
      console.error("Error fetching codebases:", error);
    } finally {
      setLoading(false);
    }
  };

  // Calls fetch functions on component mount
  useEffect(() => {
    fetchUserDetails();
    fetchCodebases();
  }, [userId]);

  // Shows loading message while data is being fetched
  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center bg-[#F7FAFC] text-[#0F172A] font-[Manrope]">
        <p className="text-sm sm:text-base text-[#64748B]">
          Loading codebases...
        </p>
      </div>
    );
  }

  // Deletes a codebase by ID after confirming with the user
  const DeleteCodebase = async (id) => {
    const sessionToken = sessionStorage.getItem("session_token");

    Swal.fire({
      title: "Are you sure you want to delete this codebase?",
      showCancelButton: true,
      confirmButtonText: "Delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          console.log(` Sending DELETE request for codebase ID: ${id}`);

          const deleteResult = await codebaseService.deleteCodebase(
            id,
            sessionToken
          );

          console.log(" Response from delete API:", deleteResult);

          setCodebases((prevCodebases) =>
            prevCodebases.filter((codebase) => codebase.id !== id)
          );

          Swal.fire("Codebase Deleted!", "", "success");
        } catch (error) {
          console.error(" Error deleting codebase:", error);
          Swal.fire(
            "Error!",
            "Failed to delete codebase. Please try again.",
            "error"
          );
        }
      }
    });
  };

  const EditCodebase = (codebaseId) => {
    //redirecting to the monaco editor with preloaded codebase content
    navigate(`/codebase/edit/${codebaseId}/data-preparation`);
  };

  const openDetailsPage = (codebaseId) => {
    // redirecting to the details page for the codebase.
    navigate(`/codebase/${codebaseId}`);
  };

  return (
    <div className=" bg-[#F7FAFC] text-[#0F172A] font-[Manrope]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* HEADER + ADD BUTTON */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-[#003153]">
              My Codebases
            </h1>
            <p className="text-sm text-[#64748B] mt-1">
              Create, manage, and edit your DEEBug projects in one place.
            </p>
          </div>

          <button
            className="inline-flex items-center justify-center bg-[#0EA5E9] text-white text-sm sm:text-base font-semibold px-4 sm:px-5 py-2.5 rounded-xl shadow-sm hover:bg-[#0284C7] transition"
            onClick={() => setPopupOpen(true)}
          >
            + Add New Codebase
          </button>
        </div>

        {/* POPUP (UNCHANGED LOGIC) */}
        <PopupGfg
          open={popupOpen}
          closePopup={() => setPopupOpen(false)}
          refreshCodebases={fetchCodebases} //  Pass refreshCodebases as a prop
        />

        {/* EMPTY STATE */}
        {codebases.length === 0 ? (
          <div className="mt-6 rounded-2xl border border-dashed border-[#CBD5E1] bg-white p-6 sm:p-8 text-center">
            <p className="font-medium text-[#475569] mb-2">
              You don&apos;t have any codebases yet.
            </p>
            <p className="text-sm text-[#64748B] mb-4">
              Click &quot;Add New Codebase&quot; to start your first DEEBug
              project.
            </p>
            <button
              className="inline-flex items-center justify-center bg-[#0EA5E9] text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm hover:bg-[#0284C7] transition"
              onClick={() => setPopupOpen(true)}
            >
              Create your first codebase
            </button>
          </div>
        ) : (
          // RESPONSIVE GRID OF CARDS
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
            {codebases.map((codebase) => (
              <div
                key={codebase.id}
                className="group bg-white rounded-2xl shadow-sm border border-[#E2E8F0] overflow-hidden flex flex-col"
              >
                {/* CARD HEADER / IMAGE AREA */}
                <div
                  className={cn(
                    "relative cursor-pointer h-40 bg-cover bg-center",
                    "bg-[url(https://images.unsplash.com/photo-1544077960-604201fe74bc?ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&ixlib=rb-1.2.1&auto=format&fit=crop&w=1651&q=80)]"
                  )}
                  onClick={() => openDetailsPage(codebase.id)}
                >
                  <div className="absolute inset-0 bg-black/50 group-hover:bg-black/60 transition" />
                  <div className="relative z-10 h-full flex flex-col justify-between p-4">
                    <div className="flex items-center gap-2 text-gray-50 text-xs">
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-[0.7rem] font-semibold">
                        {userDetails
                          ? userDetails.username?.charAt(0)?.toUpperCase()
                          : "U"}
                      </span>
                      <span className="font-medium">
                        {userDetails ? userDetails.username : "Loading..."}
                      </span>
                    </div>
                    <div>
                      <h2 className="text-lg sm:text-xl font-bold text-white">
                        {codebase.name
                          ? codebase.name
                          : `Codebase #${codebase.id}`}
                      </h2>
                      <p className="mt-1 text-xs text-gray-200 line-clamp-2">
                        {codebase.description
                          ? codebase.description
                          : "Click to view details and start editing this project in the DEEBug editor."}
                      </p>
                    </div>
                  </div>
                </div>

                {/* CARD BODY / ACTIONS */}
                <div className="flex-1 flex flex-col p-4">
                  <p className="text-xs text-[#64748B] mb-3">
                    ID: <span className="font-mono">{codebase.id}</span>
                  </p>

                  <div className="mt-auto flex flex-wrap gap-2">
                    <button
                      className="flex-1 min-w-[7rem] bg-[#003153] text-white text-xs sm:text-sm font-medium px-3 py-2 rounded-lg hover:bg-[#1D4ED8] hover:text-white transition"
                      onClick={() => EditCodebase(codebase.id)}
                    >
                      Edit in Editor
                    </button>

                    <button
                      className="flex-1 min-w-[7rem] bg-red-500 text-white text-xs sm:text-sm font-medium px-3 py-2 rounded-lg hover:bg-red-600 transition"
                      onClick={() => DeleteCodebase(codebase.id)}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Codebase;
